import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DETAIL_SHEET_CLASS } from "@/lib/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import MerchantContext from "../../../contexts/MerchantContext";
import { usePipeline } from "../../../contexts/PipelineContext";
import { trpc } from "../../../trpc";
import ConversationTimeline, {
  type TimelineItem,
} from "../../leads/chat/ConversationTimeline";
import { TICKET_PRIORITY_DISPLAY_NAMES } from "./constants";

type TicketPosition = {
  id: string;
  stage: string;
  status: string;
  order: number;
  displayName: string;
  isSystem: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  ticketId?: string;
  employees: Array<{ id: string; name: string }>;
  categories: Array<{ name: string; description: string }>;
  ticketPositions: TicketPosition[];
  // Supplied by the Tablero, where the lead lives on the same page: opening it
  // is in-page navigation, not a new tab. Omitted on /activities, which has no
  // lead surface of its own and keeps the legacy /leads deep link.
  onOpenLead?: (leadId: string) => void;
};

const CLOSED_STAGES = new Set(["success", "failure"]);
const NONE = "__none__";

const TicketDrawer = ({
  isOpen,
  onClose,
  ticketId,
  employees,
  categories,
  ticketPositions,
  onOpenLead,
}: Props) => {
  const { merchantId } = useContext(MerchantContext);
  const { allPositions } = usePipeline();
  const utils = trpc.useUtils();

  // All positions are available as manual statuses (no AI-managed restriction)
  const manualStatuses = useMemo(
    () => ticketPositions,
    [ticketPositions]
  );

  // Fetch ticket detail
  const { data: detail, isLoading: isLoadingDetail } =
    trpc.serviceRequests.getDetail.useQuery(
      { merchantId, id: ticketId || "" },
      { enabled: !!merchantId && !!ticketId }
    );

  // Fetch conversation timeline
  const { data: timelineData, isLoading: isLoadingTimeline } =
    trpc.conversations.listConversationTimeline.useQuery(
      { merchantId, leadId: detail?.leadId || "" },
      { enabled: !!merchantId && !!detail?.leadId }
    );

  const timelineItems: TimelineItem[] =
    (timelineData as TimelineItem[] | undefined) || [];

  // Editable fields state — synced from detail when it loads
  const [editStatus, setEditStatus] = useState<string>("");
  const [editCategory, setEditCategory] = useState<string>("");
  const [editPriority, setEditPriority] = useState<string>("");
  const [editEmployee, setEditEmployee] = useState<string>("");
  const [editManagedBy, setEditManagedBy] = useState<string>("human");
  const [editResolution, setEditResolution] = useState<string>("");
  const [editNotes, setEditNotes] = useState<string>("");
  const [editDocumentNumber, setEditDocumentNumber] = useState<string>("");

  // Sync form state when detail loads (or ticketId changes)
  useEffect(() => {
    if (detail) {
      setEditStatus(detail.status ?? "");
      setEditCategory(detail.category ?? "");
      setEditPriority(detail.priority ?? "");
      setEditEmployee(detail.assignedEmployeeId ?? "");
      setEditManagedBy(detail.managedBy ?? "human");
      setEditResolution(detail.resolution ?? "");
      setEditNotes(detail.notes ?? "");
      setEditDocumentNumber(detail.customerDocumentNumber ?? "");
    }
  }, [detail, ticketId]);

  const updateMutation = trpc.serviceRequests.updateTicket.useMutation();

  const handleSave = async () => {
    if (!ticketId) return;

    const data: Record<string, string> = {};
    if (editStatus && editStatus !== detail?.status) data.status = editStatus;
    if (editCategory !== (detail?.category ?? "")) data.category = editCategory;
    if (editPriority && editPriority !== detail?.priority)
      data.priority = editPriority;
    if (editEmployee !== (detail?.assignedEmployeeId ?? ""))
      data.assignedEmployeeId = editEmployee;
    if (editResolution !== (detail?.resolution ?? ""))
      data.resolution = editResolution;
    if (editManagedBy !== (detail?.managedBy ?? "human"))
      data.managedBy = editManagedBy;
    if (editNotes !== (detail?.notes ?? ""))
      data.notes = editNotes;
    if (editDocumentNumber !== (detail?.customerDocumentNumber ?? ""))
      data.customerDocumentNumber = editDocumentNumber;

    if (Object.keys(data).length === 0) {
      toast.info("No hay cambios para guardar");
      return;
    }

    try {
      await updateMutation.mutateAsync({ merchantId, id: ticketId, data });
      toast.success("Ticket actualizado");
      // Invalidate kanban columns
      utils.serviceRequests.listByStatus.invalidate();
      utils.serviceRequests.getDetail.invalidate({ merchantId, id: ticketId });
    } catch (error) {
      toast.error("Error al actualizar el ticket");
      console.error("updateTicket failed", error);
    }
  };

  // Show resolution field when status belongs to a closed stage
  const currentStatusStage = ticketPositions.find((p) => p.status === editStatus)?.stage;
  const showResolution = currentStatusStage ? CLOSED_STAGES.has(currentStatusStage) : false;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className={DETAIL_SHEET_CLASS}>
        <SheetTitle className="sr-only">
          Detalle de solicitud de servicio
        </SheetTitle>
        {isLoadingDetail || !detail ? (
          <div className="flex h-full items-center justify-center">
            <Spinner size="xl" />
          </div>
        ) : (
          <div className="flex h-full flex-col overflow-hidden md:flex-row">
            {/* Left Pane: Customer info + editable ticket fields */}
            <div className="w-full shrink-0 overflow-y-auto border-r border-neutral-300 bg-surface md:min-w-[380px] lg:w-[380px]">
              <div className="flex flex-col gap-4 p-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="mb-1 text-xl font-bold">
                      Solicitud #{detail.id.slice(0, 8)}
                    </h2>
                    <p className="text-xs text-neutral-600">
                      {new Date(detail.createdAt).toLocaleString("es-CO", {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  {detail.lead?.id &&
                    (onOpenLead ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenLead(detail.lead!.id)}
                      >
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Ver lead
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          window.open(
                            `/leads?id=${detail.lead!.id}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Ir a conversacion
                      </Button>
                    ))}
                </div>

                {/* Description */}
                {detail.description && (
                  <>
                    <div>
                      <p className="mb-1 text-sm font-semibold text-neutral-900">
                        Descripción
                      </p>
                      <p className="text-sm text-neutral-600">
                        {detail.description}
                      </p>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Customer info */}
                {detail.lead && (
                  <>
                    <div>
                      <p className="mb-3 text-sm font-semibold text-neutral-900">
                        Información del Cliente
                      </p>
                      <div className="flex flex-col gap-2">
                        <div>
                          <p className="mb-1 text-xs text-neutral-600">Nombre:</p>
                          <p className="text-sm font-semibold">
                            {detail.lead.name}
                          </p>
                        </div>
                        {detail.lead.phone && (
                          <div>
                            <p className="mb-1 text-xs text-neutral-600">
                              Teléfono:
                            </p>
                            <p className="text-sm font-semibold">
                              {detail.lead.phone}
                            </p>
                          </div>
                        )}
                        {detail.lead.email && (
                          <div>
                            <p className="mb-1 text-xs text-neutral-600">
                              Email:
                            </p>
                            <p className="text-sm font-semibold">
                              {detail.lead.email}
                            </p>
                          </div>
                        )}
                        <div>
                          <label className="mb-1 block text-xs text-neutral-600">
                            Documento de Identidad:
                          </label>
                          <Input
                            value={editDocumentNumber}
                            onChange={(e) => setEditDocumentNumber(e.target.value)}
                            placeholder="Número de documento"
                          />
                        </div>
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Property info */}
                {(detail.propertyAddress || detail.propertyCode) && (
                  <>
                    <div>
                      <p className="mb-3 text-sm font-semibold text-neutral-900">
                        Información del Inmueble
                      </p>
                      <div className="flex flex-col gap-2">
                        {detail.propertyAddress && (
                          <div>
                            <p className="mb-1 text-xs text-neutral-600">
                              Dirección:
                            </p>
                            <p className="text-sm font-semibold">
                              {detail.propertyAddress}
                            </p>
                          </div>
                        )}
                        {detail.propertyCode && (
                          <div>
                            <p className="mb-1 text-xs text-neutral-600">
                              Código:
                            </p>
                            <p className="text-sm font-semibold">
                              {detail.propertyCode}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Editable ticket fields */}
                <div>
                  <p className="mb-3 text-sm font-semibold text-neutral-900">
                    Gestión del Ticket
                  </p>
                  <div className="flex flex-col gap-3">
                    {/* Status */}
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">
                        Estado
                      </label>
                      <Select
                        value={editStatus}
                        onValueChange={setEditStatus}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                        <SelectContent>
                          {manualStatuses.map((pos) => (
                            <SelectItem key={pos.status} value={pos.status}>
                              {pos.displayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">
                        Categoría
                      </label>
                      <Select
                        value={editCategory || NONE}
                        onValueChange={(v) => setEditCategory(v === NONE ? "" : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Sin categoría</SelectItem>
                          {categories.map((c) => (
                            <SelectItem key={c.name} value={c.name}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">
                        Prioridad
                      </label>
                      <Select
                        value={editPriority}
                        onValueChange={setEditPriority}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar prioridad" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(TICKET_PRIORITY_DISPLAY_NAMES).map(
                            ([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Employee */}
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">
                        Asesor asignado
                      </label>
                      <Select
                        value={editEmployee || NONE}
                        onValueChange={(v) => setEditEmployee(v === NONE ? "" : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sin asignar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Sin asignar</SelectItem>
                          {employees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Managed by */}
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">
                        Gestionado por
                      </label>
                      <Select
                        value={editManagedBy}
                        onValueChange={setEditManagedBy}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ai">Agente AI</SelectItem>
                          <SelectItem value="human">Asesor humano</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Notes (always visible) */}
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">
                        Notas
                      </label>
                      <Textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Notas internas sobre el ticket..."
                        rows={3}
                      />
                    </div>

                    {/* Resolution (visible when closed) */}
                    {showResolution && (
                      <div>
                        <label className="mb-1 block text-xs text-neutral-600">
                          Resolución
                        </label>
                        <Textarea
                          value={editResolution}
                          onChange={(e) => setEditResolution(e.target.value)}
                          placeholder="Notas sobre la resolución del ticket..."
                          rows={3}
                        />
                      </div>
                    )}
                  </div>

                  <Button
                    className="mt-4 w-full"
                    size="sm"
                    onClick={handleSave}
                    disabled={updateMutation.isLoading}
                  >
                    {updateMutation.isLoading ? "Guardando…" : "Guardar"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Pane: Conversation timeline */}
            <div className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-neutral-100/30">
              <div className="p-4">
                <div className="rounded-md bg-surface shadow-card">
                  {detail.leadId ? (
                    <ConversationTimeline
                      timelineData={timelineItems}
                      isLoading={isLoadingTimeline}
                      allPositions={allPositions}
                    />
                  ) : (
                    <div className="flex h-64 items-center justify-center">
                      <p className="text-neutral-600">
                        No hay conversación disponible
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default TicketDrawer;
